import React, { memo, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AccessibilityInfo, Animated, findNodeHandle, Image, Platform, TouchableHighlight, View } from 'react-native';
import { TWCBackHandler } from '@components';
import { useRecoilValue } from 'recoil';

import { AlertModalTypes, alertsModalPageTestIDs, LiveControlsTestIDs, menuTestIDs, Theme } from '@constants';
import { useAllAppContextReferences, useAppContextNodeHandle, useAppContextReference, useNodeHandle } from '@hooks';
import { navigationService } from '@services';
import { screenReaderEnabledState, userSettingsState } from '@store';

import { styles } from './styles';

import SettingsIcon from '../../assets/icons/settings.svg';
import xfinityLogo from '../../assets/icons/xfinity-logo.png';
import { DEBUG, isAndroid, isWeb, isX1 } from '../../helpers/Platform';
import { FOCUS_NOW } from '../../helpers/Tools';
import { DebugMenuIDs } from '../debug/DebugMenuIDs';
import { FocusableHighlight } from '../focusable/FocusableHighlight/FocusableHighlight';
import { NavIcon } from '../icons/NavIcon';
import { MenuContainer } from '../MenuContainer/MenuContainer';
import { TWCText } from '../primitives/TWCText/TWCText';

const iconMapping = {
  settings: SettingsIcon,
};

const tPath = 'mainMenu';

const MenuItem = memo(
  ({
    testID,
    icon,
    name,
    accessibilityLabel,
    accessibilityHint,
    nextFocusLeft,
    nextFocusRight,
    nextFocusDown = null,
    isCurrent = false,
    onPress,
    onFocus,
  }) => {
    const reference = useAppContextReference(testID);
    const noFocusHandle = isAndroid ? findNodeHandle(reference?.current) : null;
    const previousHandle = useAppContextNodeHandle(nextFocusLeft);
    const nextHandle = useAppContextNodeHandle(nextFocusRight);

    return (
      <FocusableHighlight
        bottomHighlight
        accessibilityHint={accessibilityHint}
        accessibilityLabel={accessibilityLabel}
        activeOpacity={1}
        nativeID={testID}
        nextFocusDown={nextFocusDown}
        nextFocusLeft={previousHandle}
        nextFocusRight={nextHandle}
        nextFocusUp={noFocusHandle}
        ref={reference}
        style={[styles.menuItem, isCurrent && styles.menuItemCurrent]}
        testID={testID}
        underlayColor={Theme.transparent}
        onFocus={onFocus}
        onPress={onPress}
      >
        <View importantForAccessibility={'no-hide-descendants'} style={styles.menuItemContainer}>
          {icon &&
            (Platform.OS === 'web' ? (
              <Image accessibilityIgnoresInvertColors source={iconMapping[icon]} style={styles.image} />
            ) : (
              <NavIcon
                accessibilityIgnoresInvertColors
                color={Theme.whisperWhite}
                height={Theme.px(32)}
                name={icon}
                style={styles.icon}
                width={Theme.px(32)}
              />
            ))}
          {name && (
            <TWCText noTestID style={Theme.styles.headerText}>
              {name}
            </TWCText>
          )}
        </View>
      </FocusableHighlight>
    );
  },
);

export const MainMenu = ({ state, descriptors, navigation }) => {
  const menuReferences = useAllAppContextReferences();
  const currentPageID = descriptors[state.routes[state.index].key]?.route?.name;
  const currentPageReference = useAppContextReference(currentPageID);
  const currentPageTabID = descriptors[state.routes[state.index].key]?.options?.tabBarTestID;

  const menuOffset = useRef(new Animated.Value(Theme.px(-540))).current;
  const menuShown = useRef(false);

  const exitMenuReference = useRef();
  const exitMenuHandle = useNodeHandle(exitMenuReference);

  const exitLiveControlsReference = useAppContextReference(LiveControlsTestIDs.liveVideoControlsExit);
  const liveControlsReference = useAppContextReference(LiveControlsTestIDs.liveVideoControls);

  const currentRouteName = navigationService.getCurrentRoute();

  useEffect(() => {
    if (currentRouteName === alertsModalPageTestIDs.alertsModalPage) {
      hideMenu();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRouteName]);

  const menuReference = useAppContextReference(menuTestIDs.mainMenu);
  const menuHandle = useNodeHandle(menuReference);

  const hideMenuTimeout = useRef(null);

  const screenReaderEnabled = useRecoilValue(screenReaderEnabledState);

  const { t } = useTranslation('translation', {
    keyPrefix: tPath,
  });

  useEffect(
    () => () => {
      if (hideMenuTimeout.current) {
        clearTimeout(hideMenuTimeout.current);
      }
    },
    [hideMenuTimeout],
  );

  const setNewTimeout = () => {
    if (hideMenuTimeout.current) {
      clearTimeout(hideMenuTimeout.current);
    }

    // We don't want to dismiss screen reader focus, disable the feature when it is turned on
    if (!screenReaderEnabled) {
      hideMenuTimeout.current = setTimeout(() => {
        if (menuShown.current) {
          hideMenu();
          focusCurrentPage(true);
        }
      }, 5000);
    }
  };

  useEffect(() => {
    if (!screenReaderEnabled) {
      setNewTimeout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenReaderEnabled]);

  const { cableProviderId } = useRecoilValue(userSettingsState);
  const focusCurrentPage = (fromTimeout = false) => {
    if (currentPageTabID === menuTestIDs.live) {
      FOCUS_NOW(fromTimeout ? exitLiveControlsReference : liveControlsReference);
    } else {
      FOCUS_NOW(currentPageReference);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const showMenu = () => {
    menuShown.current = true;
    setNewTimeout();
    Animated.timing(menuOffset, {
      toValue: 0,
      duration: 250,
      useNativeDriver: true,
    }).start();
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const hideMenu = () => {
    const routeCurrent = navigationService.getCurrentRoute();
    const alertType = navigationService.getCurrentRouteParameters()?.type;

    if (!(routeCurrent === alertsModalPageTestIDs.alertsModalPage && alertType === AlertModalTypes.exit)) {
      menuShown.current = false;
      if (hideMenuTimeout.current) {
        clearTimeout(hideMenuTimeout.current);
      }
      Animated.timing(menuOffset, {
        toValue: Theme.px(-540),
        duration: 250,
        useNativeDriver: true,
      }).start();
    }
  };

  const showMenuReference = useAppContextReference(menuTestIDs.showMainMenu);

  useEffect(() => {
    showMenuReference.current = {
      showMenu,
      hideMenu,
    };
  }, [showMenuReference, showMenu, hideMenu]);

  const showControlsReference = useAppContextReference(LiveControlsTestIDs.showLiveVideoControls);

  const onBackPress = () => {
    if (menuShown.current) {
      const routeState = navigationService.navigationRef.getState();
      const routeCurrent = navigationService.getCurrentRoute();
      const modalOpened = isWeb
        ? routeState.routes.find(r => r.name === alertsModalPageTestIDs.alertsModalPage)
        : routeCurrent === alertsModalPageTestIDs.alertsModalPage;

      if (modalOpened) {
        navigationService.goBack();
        return true;
      } else {
        navigationService.navigate(alertsModalPageTestIDs.alertsModalPage, {
          type: AlertModalTypes.exit,
        });
        return true;
      }
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(
    useCallback(() => {
      TWCBackHandler.addEventListener('hardwareBackPress', onBackPress, 5);
      return () => TWCBackHandler.removeEventListener('hardwareBackPress', onBackPress);
    }, []),
  );

  const useTVEventHandler = require('react-native').useTVEventHandler || (() => null);

  const myTVEventHandler = event => {
    const target = event.target;
    const childNodes = state.routes.map(route => {
      const { options } = descriptors[route.key];
      const childReference = menuReferences[options.tabBarTestID]?.current;

      return childReference ? findNodeHandle(childReference) : null;
    });
    const isChild = childNodes.includes(target);
    const isMenu = menuHandle === target;
    let isDebugChild = false;

    if (DEBUG) {
      isDebugChild = target === findNodeHandle(menuReferences[menuTestIDs.debug]?.current);
    }
    if (
      event.eventType === 'focus' &&
      !isChild &&
      !isMenu &&
      !isDebugChild &&
      !currentPageTabID === menuTestIDs.live &&
      menuShown.current
    ) {
      hideMenu();
    } else if (isChild || isMenu || isDebugChild) {
      event.isMenuEvent = true;
      setNewTimeout();
    }
  };

  useTVEventHandler(myTVEventHandler);

  const routes = state.routes;

  return (
    <>
      <Animated.View style={[styles.menu, { transform: [{ translateY: menuOffset }] }]} useNativeDriver>
        <MenuContainer useGradient={currentPageTabID !== menuTestIDs.live}>
          {routes.map((route, index) => {
            const { options } = descriptors[route.key];
            const previousOptions = index > 0 ? descriptors[routes[index - 1].key]?.options : null;
            let nextOptions = index < routes.length - 1 ? descriptors[routes[index + 1].key]?.options : null;
            const label = options.title === undefined ? route.name : options.title;
            const testID = options.tabBarTestID;

            const isCurrent = state.index === state.routes.findIndex(stateRoute => stateRoute.name === route.name);

            const onPress = () => {
              const event = navigation.emit({
                type: 'tabPress',
                target: route.key,
                canPreventDefault: true,
              });

              hideMenu();

              if (!isCurrent && !event.defaultPrevented) {
                // The `merge: true` option makes sure that the params inside the tab screen are preserved
                navigation.jumpTo(route.name, { merge: true });
              } else {
                FOCUS_NOW(currentPageReference);
              }
            };

            if (DEBUG && !nextOptions) {
              nextOptions = {
                tabBarTestID: menuTestIDs.debug,
              };
            }

            const customAccessibilityHint = {
              [menuTestIDs.live]: 'liveAccessibilityHint',
              [menuTestIDs.myWeather]: 'myWeatherAccessibilityHint',
              [menuTestIDs.onDemand]: 'onDemandAccesssibilityHint',
              [menuTestIDs.settings]: 'settingsAccessibilityHint',
            }[testID];

            return (
              <MenuItem
                key={testID}
                accessibilityHint={
                  customAccessibilityHint ? t(customAccessibilityHint) : t('itemAccessibilityHint', { item: label })
                }
                accessibilityLabel={t('itemAccessibilityLabel', {
                  item: label,
                  index: index + 1,
                  length: routes.length,
                })}
                icon={options.icon}
                isCurrent={testID === currentPageTabID}
                name={options.icon ? null : label}
                nextFocusDown={exitMenuHandle}
                nextFocusLeft={previousOptions?.tabBarTestID ?? null}
                nextFocusRight={nextOptions?.tabBarTestID ?? null}
                testID={testID}
                onFocus={event => {
                  showMenu();
                  if (currentPageTabID === menuTestIDs.live) {
                    showControlsReference.current?.showControls?.();
                  }
                }}
                onPress={onPress}
              />
            );
          })}
          {DEBUG ? (
            <MenuItem
              key={'debug'}
              name={'debug'}
              nextFocusDown={exitMenuHandle}
              nextFocusLeft={menuTestIDs.settings}
              nextFocusRight={null}
              testID={menuTestIDs.debug}
              onFocus={event => {
                showMenu();
                if (currentPageTabID === menuTestIDs.live) {
                  showControlsReference.current?.showControls?.();
                }
              }}
              onPress={() => {
                navigationService.navigate(DebugMenuIDs.debugMenuPage);
                hideMenu();
              }}
            />
          ) : null}
          {isX1 || cableProviderId === 'Comcast_SSO' ? (
            <Image
              accessibilityIgnoresInvertColors
              accessibilityHint={t(`${tPath}xfinityHint`)}
              accessibilityLabel={t(`${tPath}xfinityLabel`)}
              source={xfinityLogo}
              style={styles.xfinityLogo}
              testID={menuTestIDs.xfinityLogo}
            />
          ) : null}
        </MenuContainer>
      </Animated.View>
      <TouchableHighlight
        key={menuTestIDs.mainMenu}
        {...(!isWeb && {
          accessibilityHint: t('mainMenuHint'),
          accessibilityLabel: t('mainMenuLabel'),
        })}
        activeOpacity={1}
        nativeID={menuTestIDs.mainMenu}
        nextFocusDown={null}
        nextFocusLeft={null}
        nextFocusRight={null}
        nextFocusUp={null}
        ref={menuReference}
        style={styles.exitNode}
        testID={menuTestIDs.mainMenu}
        underlayColor={Theme.transparent}
        onFocus={event => {
          if (event.tag === menuTestIDs.mainMenu || event.tag === menuHandle) {
            const menuItemReference = menuReferences[currentPageTabID];

            if (isAndroid) {
              setTimeout(() => {
                // This does not work reliably, more work needed
                AccessibilityInfo.setAccessibilityFocus(findNodeHandle(menuItemReference.current));
                FOCUS_NOW(menuItemReference);
              }, 0);
            } else {
              FOCUS_NOW(menuItemReference);
            }
          }
        }}
      >
        <View />
      </TouchableHighlight>
      <TouchableHighlight
        key={'exit_menu'}
        noTestID
        {...(!isWeb && {
          accessibilityHint: t('mainMenuDismissHint'),
          accessibilityLabel: t('mainMenuDismissLabel'),
        })}
        activeOpacity={0}
        ref={exitMenuReference}
        style={styles.exitNode}
        underlayColor={'clear'}
        onFocus={() => {
          hideMenu();
          // This does not work reliably, more work needed
          // if (isAndroid) {
          //   AccessibilityInfo.setAccessibilityFocus(currentPageHandle);
          // }
          focusCurrentPage();
        }}
      >
        <View />
      </TouchableHighlight>
    </>
  );
};
